import { ItemsPerPage } from 'constants/browse';
import React, { useMemo } from 'react';
import { CommonCard } from 'types';
import { Box, BoxProps } from '@landr/maestro';
import { css } from '@landr/styled-system/css';

export type GenericGridProps<I> = {
    items: I[];
    cardComponent: CommonCard<I>;
    fallbackComponent: React.ComponentType<any>;
    isFetching: boolean;
    page?: number;
    staticItems?: React.ReactNode[];
    countOfSkeletonCardsPerPage?: number;
};

const gridStyles = css({
    display: 'grid',
    width: '100%',
    gap: 'lg',
    gridTemplateColumns: 'repeat(auto-fill, 46%)',

    '& > *': {
        aspectRatio: '1/1',
        width: '100%',
    },

    md: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(192px, 1fr))',
    },
});

export const GridContainer = ({ children, ...props }: BoxProps) => (
    <Box className={gridStyles} {...props}>
        {children}
    </Box>
);

const GenericGrid = <I,>({
    items,
    cardComponent,
    fallbackComponent,
    isFetching,
    staticItems = [],
    page,
    countOfSkeletonCardsPerPage = ItemsPerPage,
}: GenericGridProps<I>): JSX.Element => {
    const CardComponent = cardComponent;
    const FallbackComponent = fallbackComponent;

    const skeletonData = useMemo(() => {
        const items = [];

        for (let i = 0; i < countOfSkeletonCardsPerPage; i++) {
            items.push(<FallbackComponent key={i} />);
        }

        return items;
    }, [countOfSkeletonCardsPerPage, FallbackComponent]);

    const itemsData = useMemo(() => {
        return [
            ...staticItems,
            ...items.map((item: I, index: number) => (
                <CardComponent
                    id={(item as { guid?: string })?.guid || String(index)}
                    item={item}
                    key={(item as { guid?: string })?.guid || String(index)}
                    page={page}
                    position={index}
                />
            )),
        ];
    }, [items, page, staticItems, CardComponent]);

    return (
        <GridContainer>{isFetching ? skeletonData : itemsData}</GridContainer>
    );
};

export default GenericGrid;
